import React from "react";
import { useAppSelector, useAppDispatch } from "../../../../store";
import { addBridgeSystem } from "../../../../store/features/optionalSystemsSlice";
import { BridgeSystem } from "../../../../types/optionalSystems";

import { formatCurrency } from "../../../../utils/formatting";
import { selectors } from "../../../../store/selectors";

interface BridgeSystemsProps {
  availableSystems: BridgeSystem[];
  shipTonnage: number;
  techLevel: number;
  isStreamlined?: boolean;
  tlOverrideActive?: boolean;
  sizeOverrideActive?: boolean;
}

const BridgeSystems: React.FC<BridgeSystemsProps> = ({
  availableSystems,
  shipTonnage,
  techLevel,
  isStreamlined = false,
  tlOverrideActive = false,
  sizeOverrideActive = false,
}) => {
  const dispatch = useAppDispatch();
  const shipSize = useAppSelector((state) => state.shipBuilder.shipSize);





  // Precompute selector results for each system
  const detachableStats = useAppSelector((state) =>
    Object.fromEntries(
      availableSystems
        .filter((system) => system.subtype === "detachable")
        .map((system) => [
          system.id,
          selectors.derived.selectDetachableBridgeStats(state, system),
        ]),
    ),
  );
  const holographicStats = useAppSelector((state) =>
    Object.fromEntries(
      availableSystems
        .filter((system) => system.subtype === "holographic")
        .map((system) => [
          system.id,
          selectors.derived.selectHolographicControlsStats(state, system),
        ]),
    ),
  );
  const sensorStationStats = useAppSelector((state) =>
    Object.fromEntries(
      availableSystems
        .filter((system) => system.subtype === "sensorStation")
        .map((system) => [
          system.id,
          selectors.derived.selectSensorStationStats(state, system),
        ]),
    ),
  );

  // Prepare available systems with dynamic calculations and validation
  const processedAvailableSystems = availableSystems.map((system) => {
    let calculatedCost = system.cost;
    let calculatedTonnage = system.tonnage;
    let calculatedPower = system.power;
    let calculationNote = "";
    let validationErrors: string[] = [];

    if (system.subtype === "detachable") {
      const stats = detachableStats[system.id];
      calculatedCost = stats.cost;
      calculatedTonnage = stats.tonnage;
      calculatedPower = stats.power;
      calculationNote = stats.calculationNote;
    } else if (system.subtype === "holographic") {
      const stats = holographicStats[system.id];
      calculatedCost = stats.cost;
      calculatedTonnage = stats.tonnage;
      calculatedPower = stats.power;
    } else if (system.subtype === "sensorStation") {
      const stats = sensorStationStats[system.id];
      calculatedCost = stats.cost;
      calculatedTonnage = stats.tonnage;
      calculatedPower = stats.power;
    }

    // Tech level validation
    if (!tlOverrideActive && (system.techLevel ?? 0) > techLevel) {
      validationErrors.push(`Requires TL ${system.techLevel}`);
    }

    // Size validation for sensor stations
    if (system.subtype === "sensorStation") {
      const maxShipTonnage = system.properties?.maxShipTonnage || 7500;
      if (!sizeOverrideActive && shipSize > maxShipTonnage) {
        validationErrors.push(
          `Sensor stations are only available on ships of ${maxShipTonnage} tons or less`
        );
      }
    }

    return {
      ...system,
      cost: calculatedCost,
      tonnage: calculatedTonnage,
      power: calculatedPower,
      calculationNote,
      validationErrors,
    };
  });

  const handleAddSystem = (system: BridgeSystem) => {
    let finalSystem = { ...system };
    if (system.subtype === "detachable") {
      const stats = detachableStats[system.id];
      finalSystem = {
        ...system,
        cost: stats.cost,
        tonnage: stats.tonnage,
        power: stats.power,
      };
    } else if (system.subtype === "holographic") {
      const stats = holographicStats[system.id];
      finalSystem = {
        ...system,
        cost: stats.cost,
        tonnage: stats.tonnage,
        power: stats.power,
      };
    } else if (system.subtype === "sensorStation") {
      const stats = sensorStationStats[system.id];
      finalSystem = {
        ...system,
        cost: stats.cost,
        tonnage: stats.tonnage,
        power: stats.power,
      };
    }
    // Note: We don't block on validation errors here since users can override warnings
    // Validation is only used for UI display purposes
    dispatch(addBridgeSystem(finalSystem));
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">Bridge Systems</h2>

      <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-6 bg-white dark:bg-gray-800">
        <h3 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-gray-100">
          Available Bridge Systems
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {processedAvailableSystems.map((system) => (
            <div
              key={system.id}
              className={`border rounded-lg p-4 flex flex-col justify-between ${
                system.validationErrors.length > 0 
                  ? "bg-orange-50 dark:bg-orange-900/30 border-orange-200 dark:border-orange-700" 
                  : "bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600"
              }`}
            >
              <div>
                <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                  {system.name}
                </div>
                <ul className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                  <li>
                    <b>Cost:</b> {formatCurrency(system.cost)}
                  </li>
                  <li>
                    <b>Tonnage:</b> {system.tonnage} tons
                  </li>
                  <li>
                    <b>Power:</b> {system.power}
                  </li>
                  {system.subtype === "holographic" && (
                    <li>
                      <b>Initiative DM:</b> +2
                    </li>
                  )}
                  {system.subtype === "detachable" && (
                    <>
                    </>
                  )}
                  {system.subtype === "sensorStation" && (
                    <li>
                      <b>Max Ship Size:</b> 7,500 tons
                    </li>
                  )}
                </ul>
                {system.validationErrors.length > 0 && (
                  <div className="text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded p-2 mb-2">
                    <div className="font-semibold mb-1">⚠️ Validation Warning:</div>
                    {system.validationErrors.map((err, idx) => (
                      <div key={idx} className="mb-1">{err}</div>
                    ))}
                    <div className="text-orange-600 mt-1 italic">
                      You can still add this system if needed.
                    </div>
                  </div>
                )}
                {system.subtype === "detachable" && system.calculationNote && (
                  <div className="text-xs text-blue-700 mb-2">
                    {system.calculationNote}
                  </div>
                )}
              </div>
              <button
                className={`mt-2 px-3 py-1 rounded text-white font-semibold ${
                  system.validationErrors.length > 0 
                    ? "bg-orange-500 hover:bg-orange-600" 
                    : "bg-blue-600 hover:bg-blue-700"
                }`}
                onClick={() => handleAddSystem(system as BridgeSystem)}
                title={
                  system.validationErrors.length > 0
                    ? `Warning: ${system.validationErrors.join(" ")}. Click to add anyway.`
                    : "Add system"
                }
              >
                {system.validationErrors.length > 0 ? "Add Anyway" : "Add"}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BridgeSystems;
